import React, { useState, useEffect, useRef, useCallback } from "react";
import { auth, db } from "../../firebase";
import { increment } from "firebase/firestore";
import {
  collection,
  addDoc,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  arrayUnion,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { initMercadoPago, CardPayment } from "@mercadopago/sdk-react";
import useCalculoEnvio from "../../hooks/useCalculoEnvio";
import useConfiguracionNegocio from "../../hooks/useConfiguracionNegocio";
import { guardarUltimoPedidoCliente } from "../../utils/ultimoPedidoCliente";

initMercadoPago("APP_USR-0da2a502-ec5e-4ac6-9a23-5eb713ecabde");

function Checkout() {
  const navigate = useNavigate();
  const location = useLocation();

  const { promociones = [] } = location.state || {};
  const { configuracionNegocio, estadoTienda } = useConfiguracionNegocio();
  const [usuario, setUsuario] = useState(null);

  const [tipoEntrega, setTipoEntrega] = useState("domicilio");
  const [metodoPago, setMetodoPago] = useState("tarjeta_app");

  const [domiciliosGuardados, setDomiciliosGuardados] = useState([]);
  const [esNuevaDireccion, setEsNuevaDireccion] = useState(true);

  const [direccionCompleta, setDireccionCompleta] = useState("");
  const [calleNumero, setCalleNumero] = useState("");
  const [colonia, setColonia] = useState("");
  const [indicaciones, setIndicaciones] = useState("");
  const [telefono, setTelefono] = useState("");

  const [cargando, setCargando] = useState(false);
  const [mensajeTerminal, setMensajeTerminal] = useState("");
  const [mostrarPasarela, setMostrarPasarela] = useState(false);

  const [latDestino, setLatDestino] = useState(null);
  const [lngDestino, setLngDestino] = useState(null);

  const [productosCarrito, setProductosCarrito] = useState([]);
  const [subtotalComida, setSubtotalComida] = useState(0);

  const autocompleteRef = useRef(null);
  const autocompleteListenerRef = useRef(null);
  const inputDireccionRef = useRef(null);

  const {
    costoEnvio,
    distanciaKm,
    mensajeEnvio,
    esRutaValida,
    fueraDeRango,
    calcularRuta,
    resetearEnvio,
  } = useCalculoEnvio(subtotalComida, promociones, configuracionNegocio);

  const tiendaCerrada = estadoTienda && estadoTienda.abierta === false;

  const mensajeTiendaCerradaCheckout =
    estadoTienda?.mensaje ||
    "Por ahora la tienda está cerrada y no estamos recibiendo pedidos.";

  const mensajeReaperturaCheckout = estadoTienda?.reaperturaTexto || "";

  useEffect(() => {
    try {
      const carritoGuardado =
        JSON.parse(localStorage.getItem("carritoMitico")) || [];

      setProductosCarrito(carritoGuardado);

      let totalBruto = carritoGuardado.reduce(
        (sum, item) => sum + (item.precio || 0) * item.cantidad,
        0,
      );

      let descuentoTotal = 0;

      const promos2x1 = promociones.filter((p) => p.tipo === "2x1");

      promos2x1.forEach((promo) => {
        const articulosPromo = carritoGuardado.filter(
          (item) => item.id === promo.productoId,
        );

        const cantidadGratis = Math.floor(articulosPromo.length / 2);

        if (cantidadGratis > 0) {
          descuentoTotal += (articulosPromo[0]?.precio || 0) * cantidadGratis;
        }
      });

      const totalFinal = totalBruto - descuentoTotal;

      setSubtotalComida(totalFinal > 0 ? totalFinal : 0);
    } catch (error) {
      console.error("Error leyendo carrito:", error);
    }
  }, [promociones]);

  useEffect(() => {
    const desuscribir = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/login");
        return;
      }

      setUsuario(user);

      try {
        const userDocRef = doc(db, "usuarios", user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (!userDocSnap.exists()) return;

        const data = userDocSnap.data();

        if (data.telefono) {
          setTelefono(data.telefono);
        }

        if (
          data.direcciones &&
          Array.isArray(data.direcciones) &&
          data.direcciones.length > 0
        ) {
          setDomiciliosGuardados(data.direcciones);

          const primeraDir = data.direcciones[0];

          if (primeraDir.lat && primeraDir.lng) {
            setEsNuevaDireccion(false);

            setCalleNumero(primeraDir.calle || "");
            setColonia(primeraDir.colonia || "");
            setIndicaciones(primeraDir.indicaciones || "");

            setDireccionCompleta(primeraDir.textoGoogleMaps || "");

            setLatDestino(primeraDir.lat);
            setLngDestino(primeraDir.lng);

            calcularRuta(
              primeraDir.textoGoogleMaps,
              primeraDir.lat,
              primeraDir.lng,
            );
          }
        }
      } catch (error) {
        console.error("Error cargando usuario:", error);
      }
    });

    return () => desuscribir();
  }, [navigate, calcularRuta]);

  useEffect(() => {
    if (tipoEntrega === "sucursal") {
      setMetodoPago("efectivo_sucursal");
      resetearEnvio();
      return;
    }

    setMetodoPago("tarjeta_app");

    if (direccionCompleta && latDestino && lngDestino) {
      calcularRuta(direccionCompleta, latDestino, lngDestino);
    }
  }, [
    tipoEntrega,
    direccionCompleta,
    latDestino,
    lngDestino,
    calcularRuta,
    resetearEnvio,
  ]);

  useEffect(() => {
    if (
      tipoEntrega !== "domicilio" ||
      !esNuevaDireccion ||
      !window.google ||
      !window.google.maps ||
      !window.google.maps.places ||
      !inputDireccionRef.current
    ) {
      return;
    }

    if (autocompleteRef.current) return;

    const autocomplete = new window.google.maps.places.Autocomplete(
      inputDireccionRef.current,
      {
        componentRestrictions: { country: "mx" },
        fields: ["address_components", "formatted_address", "geometry"],
        types: ["address"],
      },
    );

    autocompleteRef.current = autocomplete;

    autocompleteListenerRef.current = autocomplete.addListener(
      "place_changed",
      () => {
        const place = autocomplete.getPlace();

        if (!place || !place.geometry) {
          setMensajeTerminal(
            "⚠️ Selecciona una dirección válida del listado de Google.",
          );
          return;
        }

        let calle = "";
        let numero = "";
        let coloniaEncontrada = "";

        if (place.address_components) {
          for (const component of place.address_components) {
            const types = component.types;

            if (types.includes("route")) {
              calle = component.long_name;
            }

            if (types.includes("street_number")) {
              numero = component.long_name;
            }

            if (
              types.includes("sublocality") ||
              types.includes("neighborhood")
            ) {
              coloniaEncontrada = component.long_name;
            }
          }
        }

        const direccionTexto = place.formatted_address;
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        setDireccionCompleta(direccionTexto);
        setLatDestino(lat);
        setLngDestino(lng);
        setCalleNumero(numero ? `${calle} ${numero}` : calle);
        setColonia(coloniaEncontrada || "Sin colonia");
        setMensajeTerminal("");

        calcularRuta(direccionTexto, lat, lng);
      },
    );

    return () => {
      if (autocompleteListenerRef.current) {
        window.google.maps.event.removeListener(
          autocompleteListenerRef.current,
        );
      }

      autocompleteRef.current = null;
      autocompleteListenerRef.current = null;
    };
  }, [tipoEntrega, esNuevaDireccion, calcularRuta]);

  const handleSelectDireccion = useCallback(
    (e) => {
      const idx = e.target.value;

      if (idx === "nueva") {
        setEsNuevaDireccion(true);

        setDireccionCompleta("");
        setCalleNumero("");
        setColonia("");
        setIndicaciones("");

        setLatDestino(null);
        setLngDestino(null);

        resetearEnvio();

        return;
      }

      const seleccionada = domiciliosGuardados[idx];

      if (!seleccionada) return;

      setEsNuevaDireccion(false);

      setCalleNumero(seleccionada.calle || "");
      setColonia(seleccionada.colonia || "");
      setIndicaciones(seleccionada.indicaciones || "");
      setDireccionCompleta(seleccionada.textoGoogleMaps || "");

      setLatDestino(seleccionada.lat || null);
      setLngDestino(seleccionada.lng || null);

      if (seleccionada.lat && seleccionada.lng) {
        calcularRuta(
          seleccionada.textoGoogleMaps,
          seleccionada.lat,
          seleccionada.lng,
        );
      } else {
        resetearEnvio();

        setMensajeTerminal(
          "⚠️ Este domicilio fue guardado con una versión antigua. Por favor selecciónalo nuevamente desde Google Maps.",
        );
      }
    },
    [domiciliosGuardados, calcularRuta, resetearEnvio],
  );

  const guardarPedidoEnFirestore = async (
    pagoId = "Efectivo/Terminal",
    datosPago = {},
  ) => {
    if (!usuario) return;

    const direccionPedido =
      tipoEntrega === "domicilio"
        ? {
            calle: calleNumero,
            colonia: colonia,
            indicaciones: indicaciones,
            textoGoogleMaps: direccionCompleta,
            lat: latDestino,
            lng: lngDestino,
          }
        : null;

    const pedidoReal = {
      clienteId: usuario.uid,
      userId: usuario.uid,
      uid: usuario.uid,
      clienteEmail: usuario.email,

      telefono,

      productos: productosCarrito,

      totalComida: subtotalComida,
      costoEnvio,
      totalFinal: subtotalComida + costoEnvio,

      tipoEntrega,
      metodoPago,

      estatus: "recibido",

      pagoId,

      estadoPago:
        metodoPago === "tarjeta_app"
          ? datosPago.estadoPago || "approved"
          : "pendiente_pago",

      detallePago:
        metodoPago === "tarjeta_app"
          ? datosPago.detallePago || "sin_detalle"
          : "pago_en_sucursal",

      fechaPago: metodoPago === "tarjeta_app" ? new Date().toISOString() : null,

      fecha: new Date().toISOString(),

      direccion: direccionPedido,

      negocio: {
        nombreNegocio: configuracionNegocio.nombreNegocio || "Mítico Amorcito",
        direccionNegocio: configuracionNegocio.direccionNegocio || "",
        latNegocio: configuracionNegocio.latNegocio || null,
        lngNegocio: configuracionNegocio.lngNegocio || null,
      },
    };

    await addDoc(collection(db, "pedidos"), pedidoReal);

    const userDocRef = doc(db, "usuarios", usuario.uid);

    await setDoc(
      userDocRef,
      {
        telefono,
      },
      { merge: true },
    );

    if (
      tipoEntrega === "domicilio" &&
      esNuevaDireccion &&
      esRutaValida &&
      !fueraDeRango
    ) {
      const existe = domiciliosGuardados.some(
        (d) => d.textoGoogleMaps === direccionCompleta,
      );

      if (!existe) {
        await updateDoc(userDocRef, {
          direcciones: arrayUnion(direccionPedido),
        });
      }
    }
  };
  const handlePrendarBotonSubmit = async (e) => {
    e.preventDefault();

    setMensajeTerminal("");

    if (tiendaCerrada) {
      setMostrarPasarela(false);
      setMensajeTerminal(
        `⏰ ${mensajeTiendaCerradaCheckout}${
          mensajeReaperturaCheckout ? ` ${mensajeReaperturaCheckout}` : ""
        }`,
      );
      return;
    }

    if (subtotalComida < 1) {
      setMensajeTerminal("⚠️ Tu carrito está vacío.");
      return;
    }

    if (!telefono) {
      setMensajeTerminal("⚠️ Ingresa un teléfono de contacto.");
      return;
    }

    if (tipoEntrega === "domicilio") {
      if (!direccionCompleta) {
        setMensajeTerminal("⚠️ Selecciona una dirección válida.");
        return;
      }

      if (!esRutaValida) {
        setMensajeTerminal("⚠️ No se pudo calcular la ruta.");
        return;
      }

      if (fueraDeRango) {
        setMensajeTerminal("❌ Esta dirección está fuera de rango.");
        return;
      }
    }

    if (metodoPago === "tarjeta_app") {
      setMostrarPasarela(true);
      return;
    }

    setCargando(true);

    try {
      await guardarPedidoEnFirestore("Pago en Caja / Sucursal");

      guardarUltimoPedidoCliente(productosCarrito);

      localStorage.removeItem("carritoMitico");

      setMensajeTerminal("🎉 Pedido enviado correctamente.");

      setTimeout(() => {
        navigate("/");
      }, 3000);
    } catch (error) {
      console.error(error);

      setMensajeTerminal("❌ Error al registrar pedido.");
    } finally {
      setCargando(false);
    }
  };

  const handlePagoExitoso = async (param = {}) => {
    if (tiendaCerrada) {
      setMostrarPasarela(false);
      setMensajeTerminal(
        `⏰ ${mensajeTiendaCerradaCheckout}${
          mensajeReaperturaCheckout ? ` ${mensajeReaperturaCheckout}` : ""
        }`,
      );
      return;
    }

    setCargando(true);

    try {
      const idTransaccion = param.id || "MP-REAL-OK";

      await guardarPedidoEnFirestore(idTransaccion, {
        estadoPago: param.status || "approved",
        detallePago: param.status_detail || "accredited",
      });

      guardarUltimoPedidoCliente(productosCarrito);

      localStorage.removeItem("carritoMitico");

      setMensajeTerminal("🎉 Pago aprobado correctamente.");

      setMostrarPasarela(false);

      setTimeout(() => {
        navigate("/");
      }, 3000);
    } catch (error) {
      console.error(error);

      setMensajeTerminal("❌ El pago pasó pero el pedido no pudo registrarse.");
    } finally {
      setCargando(false);
    }
  };

  const botonBloqueado =
    cargando ||
    tiendaCerrada ||
    (tipoEntrega === "domicilio" && (!esRutaValida || fueraDeRango));

  const totalFinal =
    subtotalComida + (tipoEntrega === "domicilio" ? costoEnvio : 0);

  return (
    <div
      style={{
        backgroundColor: "#f7f7f7",
        minHeight: "100vh",
        padding: "18px",
        fontFamily: "-apple-system, sans-serif",
        color: "#111",
      }}
    >
      <div style={{ maxWidth: "620px", margin: "0 auto" }}>
        <button
          type="button"
          onClick={() => navigate("/carrito")}
          style={{
            background: "none",
            border: "none",
            color: "#666",
            fontWeight: "700",
            cursor: "pointer",
            marginBottom: "14px",
            padding: 0,
          }}
        >
          ← Volver al carrito
        </button>

        {tiendaCerrada && (
          <div
            style={{
              backgroundColor: "#fff8e1",
              border: "1px solid #ffe0a3",
              color: "#7a4b00",
              borderRadius: "14px",
              padding: "13px 14px",
              marginBottom: "14px",
              fontSize: "13px",
              fontWeight: "800",
              lineHeight: "1.45",
              boxShadow: "0 3px 10px rgba(0,0,0,0.04)",
            }}
          >
            ⏰ {mensajeTiendaCerradaCheckout}
            {mensajeReaperturaCheckout && (
              <div
                style={{
                  marginTop: "5px",
                  fontWeight: "950",
                }}
              >
                {mensajeReaperturaCheckout}
              </div>
            )}
            <div
              style={{
                marginTop: "6px",
                fontSize: "12px",
                color: "#9a5c1f",
              }}
            >
              Puedes revisar tu carrito, pero no podrás finalizar el pedido
              hasta que la tienda vuelva a estar disponible.
            </div>
          </div>
        )}

        <form onSubmit={handlePrendarBotonSubmit}>
          <section
            style={{
              backgroundColor: "#fff",
              borderRadius: "16px",
              border: "1px solid #eee",
              padding: "18px",
              marginBottom: "18px",
            }}
          >
            <h2 style={{ marginTop: 0, fontSize: "20px", fontWeight: "900" }}>
              1. Método de Entrega
            </h2>

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                type="button"
                onClick={() => setTipoEntrega("domicilio")}
                style={{
                  flex: 1,
                  padding: "14px",
                  borderRadius: "10px",
                  border:
                    tipoEntrega === "domicilio"
                      ? "2px solid #111"
                      : "1px solid #ccc",
                  backgroundColor: "#fff",
                  fontWeight: "900",
                  cursor: "pointer",
                }}
              >
                🛵 A Domicilio
              </button>

              <button
                type="button"
                onClick={() => setTipoEntrega("sucursal")}
                style={{
                  flex: 1,
                  padding: "14px",
                  borderRadius: "10px",
                  border:
                    tipoEntrega === "sucursal"
                      ? "2px solid #111"
                      : "1px solid #ccc",
                  backgroundColor: "#fff",
                  fontWeight: "900",
                  cursor: "pointer",
                }}
              >
                🏪 Recoger en Sucursal
              </button>
            </div>
          </section>

          <section
            style={{
              backgroundColor: "#fff",
              borderRadius: "16px",
              border: "1px solid #eee",
              padding: "18px",
              marginBottom: "18px",
            }}
          >
            <h2 style={{ marginTop: 0, fontSize: "20px", fontWeight: "900" }}>
              2. Datos de Entrega
            </h2>

            <label style={{ fontWeight: "800", fontSize: "14px" }}>
              Teléfono Celular *
            </label>
            <input
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              placeholder="Ej. 3312345678"
              style={{
                width: "100%",
                padding: "13px",
                margin: "8px 0 16px 0",
                borderRadius: "10px",
                border: "1px solid #ccc",
                boxSizing: "border-box",
              }}
            />

            {tipoEntrega === "domicilio" && (
              <>
                {domiciliosGuardados.length > 0 && (
                  <>
                    <label
                      style={{
                        fontWeight: "800",
                        fontSize: "14px",
                        color: "#0057ff",
                      }}
                    >
                      Mis Domicilios Guardados
                    </label>

                    <select
                      onChange={handleSelectDireccion}
                      value={esNuevaDireccion ? "nueva" : "0"}
                      style={{
                        width: "100%",
                        padding: "13px",
                        margin: "8px 0 16px 0",
                        borderRadius: "10px",
                        border: "1px solid #0057ff",
                        boxSizing: "border-box",
                        backgroundColor: "#fff",
                      }}
                    >
                      <option value="nueva">
                        ➕ Utilizar otra dirección nueva...
                      </option>

                      {domiciliosGuardados.map((dir, idx) => (
                        <option key={idx} value={idx}>
                          📍{" "}
                          {dir.calle ||
                            dir.textoGoogleMaps ||
                            "Domicilio guardado"}
                        </option>
                      ))}
                    </select>
                  </>
                )}

                {esNuevaDireccion ? (
                  <>
                    <label style={{ fontWeight: "800", fontSize: "14px" }}>
                      Buscar Nueva Dirección en Google Maps *
                    </label>

                    <input
                      ref={inputDireccionRef}
                      defaultValue={direccionCompleta}
                      placeholder="Escribe y selecciona tu dirección..."
                      style={{
                        width: "100%",
                        padding: "13px",
                        margin: "8px 0 16px 0",
                        borderRadius: "10px",
                        border: direccionCompleta
                          ? "1px solid #ccc"
                          : "2px solid #ff3333",
                        boxSizing: "border-box",
                      }}
                    />
                  </>
                ) : (
                  <div
                    style={{
                      backgroundColor: "#eef4ff",
                      border: "1px solid #0057ff",
                      color: "#0057ff",
                      padding: "12px",
                      borderRadius: "10px",
                      fontSize: "13px",
                      fontWeight: "700",
                      marginBottom: "16px",
                    }}
                  >
                    Dirección seleccionada: {direccionCompleta}
                  </div>
                )}

                <label style={{ fontWeight: "800", fontSize: "14px" }}>
                  Número interior, departamento o referencias
                </label>

                <textarea
                  value={indicaciones}
                  onChange={(e) => setIndicaciones(e.target.value)}
                  placeholder="Ej. Interior 4B, portón negro..."
                  rows="3"
                  style={{
                    width: "100%",
                    padding: "13px",
                    marginTop: "8px",
                    borderRadius: "10px",
                    border: "1px solid #ccc",
                    boxSizing: "border-box",
                    fontFamily: "inherit",
                  }}
                />
              </>
            )}

            {tipoEntrega === "sucursal" && (
              <div
                style={{
                  backgroundColor: "#f4f4f4",
                  padding: "14px",
                  borderRadius: "10px",
                  fontSize: "14px",
                  fontWeight: "700",
                  lineHeight: "1.5",
                }}
              >
                Recogerás tu pedido en{" "}
                {configuracionNegocio.nombreNegocio || "el restaurante"}.
                {configuracionNegocio.direccionNegocio && (
                  <div
                    style={{
                      marginTop: "6px",
                      color: "#555",
                      fontSize: "13px",
                      fontWeight: "600",
                    }}
                  >
                    📍 {configuracionNegocio.direccionNegocio}
                  </div>
                )}
              </div>
            )}
          </section>

          <section
            style={{
              backgroundColor: "#fff",
              borderRadius: "16px",
              border: "1px solid #eee",
              padding: "18px",
              marginBottom: "18px",
            }}
          >
            <h2 style={{ marginTop: 0, fontSize: "20px", fontWeight: "900" }}>
              3. Método de Pago
            </h2>

            {tipoEntrega === "sucursal" && (
              <label
                style={{
                  display: "flex",
                  gap: "10px",
                  alignItems: "center",
                  fontWeight: "900",
                  marginBottom: "12px",
                }}
              >
                <input
                  type="radio"
                  checked={metodoPago === "efectivo_sucursal"}
                  onChange={() => {
                    setMetodoPago("efectivo_sucursal");
                    setMostrarPasarela(false);
                  }}
                />
                🏪 Pagar en sucursal al recoger
              </label>
            )}

            <label
              style={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                fontWeight: "900",
              }}
            >
              <input
                type="radio"
                checked={metodoPago === "tarjeta_app"}
                onChange={() => setMetodoPago("tarjeta_app")}
              />
              💳 Pagar desde la App
            </label>

            {tipoEntrega === "sucursal" && (
              <p style={{ marginBottom: 0, color: "#666", fontSize: "13px" }}>
                Puedes pagar al recoger tu pedido o dejarlo pagado desde la app.
              </p>
            )}
          </section>

          <div style={{ padding: "4px 4px 16px 4px" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#666" }}>Subtotal Comida:</span>
              <strong>${subtotalComida.toFixed(2)} MXN</strong>
            </div>

            {tipoEntrega === "domicilio" && (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "6px",
                  }}
                >
                  <span style={{ color: "#666" }}>
                    Costo de envío ({distanciaKm || 0} km):
                  </span>

                  <strong
                    style={{
                      color: fueraDeRango ? "#c5221f" : "#555",
                    }}
                  >
                    {fueraDeRango
                      ? "Fuera de rango"
                      : `$${costoEnvio.toFixed(2)} MXN`}
                  </strong>
                </div>

                {mensajeEnvio && (
                  <div
                    style={{
                      marginTop: "8px",
                      color: fueraDeRango ? "#c5221f" : "#137333",
                      fontWeight: "800",
                      fontSize: "13px",
                    }}
                  >
                    {mensajeEnvio}
                  </div>
                )}
              </>
            )}

            <hr style={{ border: "none", borderTop: "1px solid #ddd" }} />

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "22px",
                fontWeight: "900",
              }}
            >
              <span>Total a Liquidar:</span>
              <span>${totalFinal.toFixed(2)} MXN</span>
            </div>
          </div>

          {mensajeTerminal && (
            <div
              style={{
                backgroundColor: mensajeTerminal.includes("🎉")
                  ? "#e6f4ea"
                  : "#fce8e6",
                color: mensajeTerminal.includes("🎉") ? "#137333" : "#c5221f",
                padding: "12px",
                borderRadius: "10px",
                fontWeight: "800",
                fontSize: "13px",
                marginBottom: "14px",
              }}
            >
              {mensajeTerminal}
            </div>
          )}

          {!mostrarPasarela && (
            <button
              type="submit"
              disabled={botonBloqueado}
              style={{
                width: "100%",
                backgroundColor: botonBloqueado ? "#aaa" : "#000",
                color: "#fff",
                border: "none",
                borderRadius: "12px",
                padding: "16px",
                fontSize: "16px",
                fontWeight: "900",
                cursor: botonBloqueado ? "not-allowed" : "pointer",
              }}
            >
              {tiendaCerrada
                ? "Tienda cerrada por ahora"
                : metodoPago === "tarjeta_app"
                  ? "Ir a pagar con Mercado Pago"
                  : "Enviar Pedido"}
            </button>
          )}
        </form>

        {mostrarPasarela && (
          <div
            style={{
              backgroundColor: "#fff",
              padding: "18px",
              borderRadius: "16px",
              marginTop: "18px",
              border: "1px solid #eee",
            }}
          >
            <button
              type="button"
              onClick={() => setMostrarPasarela(false)}
              style={{
                marginBottom: "14px",
                background: "none",
                border: "none",
                color: "#666",
                fontWeight: "800",
                cursor: "pointer",
              }}
            >
              ← Volver a datos de entrega
            </button>

            <CardPayment
              initialization={{
                amount: Number(totalFinal.toFixed(2)),
              }}
              onSubmit={async (formData) => {
                setCargando(true);
                setMensajeTerminal("");

                if (tiendaCerrada) {
                  setCargando(false);
                  setMostrarPasarela(false);
                  setMensajeTerminal(
                    `⏰ ${mensajeTiendaCerradaCheckout}${
                      mensajeReaperturaCheckout
                        ? ` ${mensajeReaperturaCheckout}`
                        : ""
                    }`,
                  );
                  return;
                }

                try {
                  const respuesta = await fetch(
                    "https://procesarpagomitico-t54gndn3bq-uc.a.run.app",
                    {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify({
                        token: formData.token,
                        issuer_id: formData.issuer_id,
                        payment_method_id: formData.payment_method_id,
                        transaction_amount: Number(totalFinal.toFixed(2)),
                        installments: Number(formData.installments || 1),
                        payer: {
                          email: usuario?.email || "anonimo@miticoamorcito.com",
                        },
                      }),
                    },
                  );

                  const data = await respuesta.json();

                  console.log("Respuesta pago Mercado Pago:", data);

                  if (!respuesta.ok) {
                    throw new Error(
                      data.message || "Error procesando el pago.",
                    );
                  }

                  if (data.status !== "approved") {
                    setMensajeTerminal(
                      `❌ El pago no fue aprobado. Estado: ${data.status_detail || data.status}`,
                    );
                    return;
                  }

                  await handlePagoExitoso({
                    id: data.id,
                    status: data.status,
                    status_detail: data.status_detail,
                  });
                } catch (error) {
                  console.error("Error cobrando con Mercado Pago:", error);
                  setMensajeTerminal("❌ No se pudo procesar el pago.");
                } finally {
                  setCargando(false);
                }
              }}
              onError={(error) => {
                console.error(error);
                setMensajeTerminal("❌ Error en Mercado Pago.");
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default Checkout;
